import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginHeader from './components/LoginHeader';
import LoginForm from './components/LoginForm';
import AIShowcase from './components/AIShowcase';
import OnboardingModal from './components/OnboardingModal';
import Footer from '../../components/Footer';
import { useAuth } from '../../components/AuthProvider';
import { consumeAuthReturnPath, normalizeAuthReturnPath } from '../../lib/authReturnPath';

const VOID_BG = '#080810';

const UserLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingUserId, setOnboardingUserId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const restoredSessionHandled = useRef(false);
  const {
    user: authUser,
    userProfile,
    loading: authLoading,
    profileLoading,
    profileError,
    signIn,
  } = useAuth();

  React.useEffect(() => {
    if (
      !authLoading &&
      !profileLoading &&
      authUser &&
      !restoredSessionHandled.current
    ) {
      restoredSessionHandled.current = true;
      setIsLoading(false);
      setShowWelcome(true);
      setTimeout(() => setShowWelcome(false), 3000);
      const redirectTo = location.state?.from
        ? normalizeAuthReturnPath(location.state.from, '/command-center-ai-execution-interface')
        : consumeAuthReturnPath('/command-center-ai-execution-interface');

      if (userProfile?.onboarding_complete === true || profileError) {
        navigate(redirectTo, { replace: true });
      } else {
        setOnboardingUserId(authUser.id);
        setShowOnboarding(true);
      }
    }
  }, [authLoading, authUser, location.state, navigate, profileError, profileLoading, userProfile]);

  const handleLogin = async (userData) => {
    setIsLoading(true);
    setAuthError('');

    const { error } = await signIn(userData?.email, userData?.password);

    if (error) {
      setAuthError(error?.message || 'Authentication failed. Please check your credentials.');
      setIsLoading(false);
      return;
    }

    // The root AuthProvider owns session/profile resolution. Its effect above
    // performs the redirect only after both are settled.
  };

  return (
    <div className="min-h-screen page-fade-in" style={{ background: VOID_BG }}>
      {showWelcome && (
        <div style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999, background: 'rgba(45,200,150,0.12)', border: '1px solid rgba(45,200,150,0.5)', borderRadius: '6px', padding: '12px 20px', fontFamily: "'Share Tech Mono', monospace", fontSize: '13px', color: '#2DC896', letterSpacing: '0.08em', boxShadow: '0 0 20px rgba(45,200,150,0.3)' }}>WELCOME TO RECALLCORE - SYSTEM READY</div>
      )}
      {showOnboarding && onboardingUserId && (
        <OnboardingModal
          onComplete={() => setShowOnboarding(false)}
        />
      )}
      <LoginHeader />
      <div className="flex min-h-screen flex-col pt-16">
        <main className="grid min-h-[calc(100dvh-4rem)] lg:grid-cols-12">
          <section className="flex items-center justify-center px-4 py-8 sm:px-6 lg:col-span-5 lg:p-8" style={{ background: VOID_BG }}>
            <div className="w-full max-w-md">
            {authError && (
                <div className="mb-4 p-3" style={{ background: 'rgba(255,68,68,0.1)', border: '1px solid rgba(255,68,68,0.3)' }}>
                <p style={{ fontSize: '0.75rem', color: '#FF4444', fontFamily: "'JetBrains Mono', monospace" }}>{authError}</p>
              </div>
            )}
            <LoginForm onLogin={handleLogin} isLoading={isLoading} />
          </div>
          </section>
          <section className="hidden lg:col-span-7 lg:block" style={{ background: '#0D0D1A', borderLeft: '1px solid rgba(0,255,229,0.15)' }}>
            <AIShowcase />
          </section>
          <section className="p-6 lg:hidden" style={{ background: '#0D0D1A', borderTop: '1px solid rgba(0,255,229,0.15)' }}>
            <div className="text-center">
              <h3 style={{ fontFamily: "'Orbitron', monospace", fontWeight: 700, fontSize: '1rem', color: '#E8F4FF', marginBottom: '0.5rem' }}>
                JOIN THE NETWORK
              </h3>
              <p style={{ fontSize: '0.75rem', color: '#8899AA', fontFamily: "'JetBrains Mono', monospace", marginBottom: '1rem' }}>
                82+ AI agents ready to execute your missions
              </p>
              <div className="flex justify-center space-x-6">
                {[
                  { val: '82+', label: 'AI AGENTS' },
                  { val: '10K+', label: 'OPERATORS' },
                  { val: '95%', label: 'SUCCESS RATE' },
                ]?.map(stat => (
                  <div key={stat?.label} className="text-center">
                    <p style={{ fontFamily: "'Orbitron', monospace", fontWeight: 700, fontSize: '1.2rem', color: '#00FFE5' }}>{stat?.val}</p>
                    <p style={{ fontSize: '0.6rem', color: '#8899AA', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em' }}>{stat?.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default UserLogin;
